import {
  blackoutOption,
  type CrossingKind,
  REFUSED_ALREADY_FIT,
  type RestEnding,
  restingOption,
  settledWithYou,
  wakingLine,
} from "../hud/crossing";
import {
  type EventId,
  HEAL_DAYS_AT_SLOWEST,
  type RestReading,
  type WorldDateView,
} from "../world/types";
import { type FastForwardHost, runStepped } from "./fastforward";

/*
 * The two crossings the body asks for: out cold, and lying up until it is over.
 *
 * Chunk Fx-5, out of the owner's second session — *"suddenly my screen went
 * black and stayed black for like 5 minutes … no indication of if I died, was
 * arrested etc."* — which was the blackout beat working exactly as wave 2D-a
 * wrote it. §8.2's *given in to pain* is a state the simulation puts a body in;
 * what a client owes it is a screen that goes away and time that passes, and
 * what it owed and did not give was a **sentence saying so**.
 *
 * The ruling was *"keep it, and make the blackout speak. Offer an option to skip
 * time till recovered as well"*, so this file is one machine with two ways in:
 *
 * - **the blackout**, which nobody asked for. It opens when the simulation says
 *   the player is under and closes when it says they are up — a read rather than
 *   a clock, and unchanged by this chunk;
 * - **the rest**, which somebody pressed a key for. It opens while the
 *   simulation calls the player unfit and closes when it stops.
 *
 * They share everything else — the slices, the fade, the line in the dark, the
 * morning guard and the sentence at the end — because they are the same
 * situation seen from two sides: the world is running and the player is not
 * watching it.
 *
 * # Every tick genuinely runs
 *
 * Plan decision 8, and it is the whole reason this is a state machine advanced
 * against a frame budget rather than a date being set. The wounds close on their
 * own schedule, the town goes on doing what it was doing, and whoever knocked
 * the player down is still standing there when they get up — and the replay of a
 * session that was out cold verifies exactly like one that was not
 * (`host/fastforward.ts`).
 *
 * # Nothing here decides how hurt anybody is
 *
 * Not one number in this file is about a body. *Under* and *unfit* are bits the
 * simulation set ({@link RestReading}); the one number it does own is a **guard**
 * on how long waiting can go on being the answer, and even that is the
 * simulation's own healing span read across the wall
 * ({@link REST_MORNING_LIMIT}).
 *
 * # Everything arrives as a parameter
 *
 * `host/fastforward.ts`'s rule, for its reason: the world, the clock and the
 * reading are all injected, so `dev/selfcheck/host.ts` drives a blackout, a
 * rest, five endings, a season of mornings and a guard that latches instantly
 * and with no session behind them.
 */

/**
 * How much world a crossing runs before it looks up, in seconds of stepped time.
 *
 * A slice rather than a budget, because the stopping condition has to be
 * **asked** between slices: a body comes up when the simulation says its pain
 * has fallen back under §8.2's threshold, and that is a read rather than a
 * clock. Half a minute of world time is a few hundred ticks — cheap enough to
 * run several times a frame, fine enough that nobody sleeps through an hour they
 * did not need to.
 *
 * Wave 2D-a's number, unchanged: the defect was never the slice.
 */
export const SLICE_SECONDS = 30;

/**
 * How many mornings a crossing may watch go by before it gives up: a season and
 * one.
 *
 * **Derived, not picked — and the thing it replaces is why that matters.** Wave
 * 2D-a's guard was `14 * 24 * 60 * 60` *seconds of stepped time*, of which a
 * world day is 1,200 (`TICKS_PER_DAY / TICK_HZ`), so the fortnight it was
 * written as was **1,008 world days** and never once fired; and it was never
 * reset, so the first time it did, every later blackout would have ended on its
 * first slice with the simulation still saying *senseless*.
 *
 * Both halves are fixed here. It counts **date turns observed between slices**,
 * which is `host/fastforward.ts`'s own trick and keeps the promise that the
 * client owns no calendar arithmetic — it never converts anything into days, it
 * watches the world's date change. And it is reset by {@link Rest.begin}, so a
 * count is always a count of *this* crossing.
 *
 * The size is the simulation's: `Afflictions::HEAL_DAYS_AT_SLOWEST` is the span
 * the slowest tissue heals over, and pain is what is left of the wound and
 * nothing else moves it — so once it has run, every wound is at its floor, the
 * total cannot fall any further, and a body still under is under for good. One
 * morning past it, because a crossing that opens at dusk sees its first turn a
 * few hours in and would otherwise be a morning short of the season.
 *
 * A guard rather than a rule: how long somebody is out is the simulation's
 * business, and what this stops is a screen that is dark for ever with nothing
 * to say about it.
 */
export const REST_MORNING_LIMIT = HEAL_DAYS_AT_SLOWEST + 1;

/**
 * Which crossing is running, or `"awake"` for the frame nobody is in the dark.
 *
 * Not exported: what the outside asks is whether anything is crossing
 * ({@link Rest.crossing}) and which words the dark says ({@link Rest.kind}). The
 * three-state spelling is this machine's own business, and keeping it so is what
 * stops a caller growing a fourth answer to *which crossing is this*.
 */
type RestState = "awake" | "under" | "resting";

/**
 * What the crossing asks of the world, and the whole of what it may do to it.
 *
 * Three members and two of them are reads. The third is the one command a
 * crossing sends, and it is sent **once, as the dark comes down** — see
 * {@link RestWorld.stand}.
 */
export interface RestWorld {
  /** What the simulation says about the player's body, now. */
  read(): RestReading;

  /**
   * The world's own words about a record, or `null` where it has none.
   *
   * The town's board, which is where a client that reports what happened to
   * somebody gets its sentence (`witness/witness.ts`'s `epitaph`, and the death
   * screen's own rule): **nothing about a taking is composed by a renderer.**
   */
  said(record: EventId): string | null;

  /**
   * States where the player is standing, once, as a crossing opens.
   *
   * The existing trigger at its existing cadence and nothing more
   * (`witness/witness.ts`'s `standIfWatched`, which sends only while somebody
   * needs the tile). It is here because of what chunk Fx-2 filed: the
   * fast-forward host never streams the stand, so a player who crosses time is
   * unreachable for the length of it — and a body that is lying still is a body
   * whose stated tile stays true, so one statement at the start is exactly the
   * right amount. The walk that got them here is the only thing that could have
   * made the last one stale.
   */
  stand(): void;
}

/**
 * One crossing in the dark, advanced a frame at a time.
 *
 * Held open across frames like a day crossing and for the same reason: the
 * caller keeps drawing while the world runs, which is what lets the fade come
 * down and the line in the dark breathe over a stretch of time that genuinely
 * happened.
 */
export class Rest {
  private readonly host: FastForwardHost;
  private readonly world: RestWorld;

  private state: RestState = "awake";

  /**
   * Whether the crossing goes on once the player comes round.
   *
   * **The option the ruling added** — *rest on until you are fit* — and it is an
   * option rather than a change of behaviour: the default is `false` and a
   * blackout still ends when the simulation says the player is up, which is what
   * makes it something a player chose rather than something done to them.
   *
   * It survives a blow that puts a resting player back under, because being
   * knocked out does not change what they asked for.
   */
  private onward = false;

  /** Date turns seen since this crossing opened ({@link REST_MORNING_LIMIT}). */
  private mornings = 0;

  /** The date the last slice ended on, which is all the calendar this file keeps. */
  private seen: WorldDateView | null = null;

  /**
   * Whether the guard has fired and the simulation has not said *up* since.
   *
   * **The latch**, and it is what the fortnight guard needed and did not have: a
   * blackout that gives up is a blackout over a body the simulation still calls
   * senseless, so without this the next frame would re-open it, run one slice,
   * give up again and post the notice again — for ever, at frame rate. Cleared
   * the first time the bit is genuinely gone, so the door still works for the
   * next blow.
   */
  private stuck = false;

  /** What the town held against the player, and had taken them for, going under. */
  private wantedThen: string | null = null;
  private takenThen: EventId | null = null;

  constructor(host: FastForwardHost, world: RestWorld) {
    this.host = host;
    this.world = world;
  }

  /** Whether the world is being run with the screen dark right now. */
  get crossing(): boolean {
    return this.state !== "awake";
  }

  /**
   * Which words the dark says, or `null` while nobody is in it.
   *
   * The state as the *crossing line* needs it (`hud/crossing.ts`), which is the
   * only thing outside this file that has any business knowing which of the two
   * is running — everything else asks {@link Rest.crossing}.
   */
  get kind(): CrossingKind | null {
    return this.state === "awake" ? null : this.state === "under" ? "under" : "rest";
  }

  /**
   * The second line in the dark: what the one key that still means something
   * would do.
   *
   * `null` while this machine is running nothing, which includes every camp and
   * every wait: those are crossings somebody chose the end of when they pressed
   * the key, and there is nothing left to offer them.
   */
  option(touch: boolean): string | null {
    switch (this.state) {
      case "under":
        return blackoutOption(touch, this.onward);
      case "resting":
        return restingOption(touch);
      default:
        return null;
    }
  }

  /**
   * Answers the rest key. Returns what to say, or `null` where the dark says it.
   *
   * Three answers for three states, and the middle one is the option: **out
   * cold, the key toggles whether the crossing goes on**; resting, it gets the
   * player up; awake, it starts a rest or says why there is nothing to rest
   * through.
   *
   * The toggle says nothing back, and that is deliberate rather than an
   * omission: a notice under the fade is a notice nobody can read (the whole of
   * chunk Fx-2's finding), so what answers the press is the second line
   * ({@link Rest.option}) changing under the player's eyes.
   */
  press(): string | null {
    switch (this.state) {
      case "under":
        this.onward = !this.onward;

        return null;

      case "resting":
        return this.close("up");

      default:
        if (!this.world.read().unfit) {
          return REFUSED_ALREADY_FIT;
        }

        this.begin("resting");

        return null;
    }
  }

  /**
   * Runs the world while the player cannot watch it, and returns what to say
   * when the dark lifts.
   *
   * Called first thing in a frame, before the input, so a blow that landed on
   * the last step takes this frame away — which is the third of the three ways a
   * rest ends, and it needs no words of its own because the line in the dark
   * changes to say it.
   *
   * `halted` is a session standing at its own death screen (`main.ts`'s
   * `mourning`): the world stops there, so the crossing does too, silently. The
   * figure whose pain this was is dead and the next life starts fit.
   */
  advance(deadline: number, halted: boolean): string | null {
    const under = this.world.read().under;

    // The latch clears the moment the simulation genuinely says *up*, and never
    // on anything this file decided.
    if (!under) {
      this.stuck = false;
    }

    if (halted) {
      this.state = "awake";

      return null;
    }

    if (under && !this.stuck && this.state !== "under") {
      if (this.state === "awake") {
        // A blow out of nowhere: a fresh crossing, with the option standing
        // where it always starts ({@link Rest.begin} puts it back).
        this.begin("under");
      } else {
        // And a blow on a body that had already lain down. The crossing carries
        // on as it was — the count, the record and what they asked for all
        // survive it, because what happened to them while they were down began
        // when they lay down and being knocked out does not change their mind.
        this.onward = true;
        this.state = "under";
      }
    }

    if (this.state === "awake") {
      return null;
    }

    while (this.host.now() < deadline) {
      runStepped(this.host, SLICE_SECONDS);
      this.countTheMorning();

      if (this.mornings > REST_MORNING_LIMIT) {
        // Said once and then latched: a body that has not come round in a season
        // is a body somebody needs to know about, and a body that will not come
        // round is one this client must stop taking the screen away for.
        this.stuck = true;

        return this.close("stuck");
      }

      const reading = this.world.read();

      if (this.state === "under") {
        if (reading.under) {
          continue;
        }

        if (!this.onward) {
          return this.close("round");
        }

        // The option, spent: the fade never lifts and the crossing goes on as
        // the thing the player asked for.
        this.state = "resting";
      }

      if (!reading.unfit) {
        return this.close("fit");
      }

      if (reading.reached) {
        return this.close("roused");
      }
    }

    return null;
  }

  /**
   * Opens a crossing: the count, the date it is counted from, and what the town
   * held against the player at the moment the dark came down.
   *
   * The *before* half is the only way a client can date what the town did: an
   * arrest record is the last one on the player's name whenever it was written
   * (`world/types.ts`'s `arrestOf`), so what makes it *news* is that it was not
   * there when they went under.
   */
  private begin(state: RestState): void {
    const reading = this.world.read();

    this.state = state;
    this.onward = false;
    this.mornings = 0;
    this.seen = this.host.date();
    this.wantedThen = reading.wanted;
    this.takenThen = reading.taken;
    this.world.stand();
  }

  /** Closes it, and says what happened — including what the town did. */
  private close(ending: RestEnding): string {
    const said = wakingLine(ending, this.host.date(), this.townsAnswer());

    this.state = "awake";
    this.onward = false;
    this.mornings = 0;
    this.seen = null;

    return said;
  }

  /**
   * What the town did while nobody could see it, or `null` where it did nothing.
   *
   * Two answers and an honest silence:
   *
   * - **a taking**, where a record has appeared on the player's name since the
   *   dark came down — said in the log's own words, which is the epitaph's rule
   *   one record over;
   * - **a reprimand**, where the town wanted them going under and does not now.
   *   That answer is *recordless* by design (`sim_core::watch`: everything below
   *   a taking is seen and never logged), so the client says it in the
   *   accusation's own register and says it about the crime it watched — the
   *   only sentence in this whole crossing composed on this side of the wall;
   * - **nothing**, where the town never reached them, which is nearly always.
   */
  private townsAnswer(): string | null {
    const reading = this.world.read();

    if (reading.taken !== null && reading.taken !== this.takenThen) {
      return this.world.said(reading.taken);
    }

    return this.wantedThen !== null && reading.wanted === null
      ? settledWithYou(this.wantedThen)
      : null;
  }

  /**
   * Counts a date turn, which is the only thing this file knows about time.
   *
   * Deliberately not arithmetic: the client is never told how many ticks a day
   * is and does not want to be (`host/fastforward.ts`'s own note), so a morning
   * is *the date being different from the one the last slice ended on*. A world
   * with no calendar answers `null` for ever and is counted as no mornings at
   * all — which is safe here, because a world with no history has no figure to
   * knock down.
   */
  private countTheMorning(): void {
    const now = this.host.date();

    if (now === null) {
      return;
    }

    if (this.seen !== null && (now.year !== this.seen.year || now.dayOfYear !== this.seen.dayOfYear)) {
      this.mornings += 1;
    }

    this.seen = now;
  }
}
